import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  viewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import Chart from "chart.js/auto";
import { ChartConfiguration, ChartDataset } from "chart.js";

import { Widget } from "../../../dashboard/models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { ArticleFacadeService } from "../../services/article/article-facade.service";

// Bouton filtre dans le header
import { SharedModule } from "../../../../shared/shared.module";
import { PopoverModule } from "../../../../shared/modules/popover/popover.module";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { parseNumber } from "../../../../shared/utils/number-utils.service";

type TimeScale = "DAY" | "WEEK" | "MONTH";

@Component({
  selector: "vl-widget-ca-par-periode-article",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    PopoverModule,
    WidgetTitleComponent,
    ChipsComponent,
    ChipsToggleComponent,
  ],
  host: { class: "widget-component" },
  styleUrls: ["./ca-par-periode-article.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./ca-par-periode-article.component.html",
})
export class CaParPeriodeArticleComponent implements OnDestroy {
  // ==== Inputs ====
  articleFacade = inject(ArticleFacadeService);
  stats = this.articleFacade.getState().stats;
  metadata = input<EntityMetadata>();
  widget = input<Widget>();

  // ==== UI / chart ====
  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("caperiode");
  private chart?: Chart<"bar", number[], string>;
  private elRef = inject(ElementRef<HTMLElement>);

  // ==== Filtres ====
  timeScale = signal<TimeScale>("MONTH");
  timeScaleChoices = [
    { name: "Jour", value: "DAY" },
    { name: "Semaine", value: "WEEK" },
    { name: "Mois", value: "MONTH" },
  ];

  // ==== Données agrégées (avec zero-fill entre min/max) ====
  points = computed(() => {
    const lignes = (this.stats()?.facturesLignes ?? []) as any[];
    const scale = this.timeScale();
    const byKey = new Map<string, { d: Date; amount: number }>();

    for (const fl of lignes) {
      const f = fl?.facture;
      const d0 = parseDate(f?.date ?? f?.dateString ?? fl?.createdAt);
      if (!d0) continue;

      const d = startOf(d0, scale);
      const key = makeKey(d, scale);

      const q = parseNumber(fl?.quantite ?? 0);
      const pu = parseNumber(fl?.prix ?? 0);
      let amount = q * pu;

      const cur = byKey.get(key);
      if (cur) cur.amount += amount;
      else byKey.set(key, { d, amount });
    }

    if (!byKey.size) return [] as { d: Date; label: string; amount: number }[];

    // range min/max
    const arr = [...byKey.values()].sort(
      (a, b) => a.d.getTime() - b.d.getTime(),
    );
    const minD = arr[0].d;
    const maxD = arr.at(-1)!.d;

    // timeline + zero-fill
    const out: { d: Date; label: string; amount: number }[] = [];
    for (let d = new Date(minD); d <= maxD; d = addOne(d, scale)) {
      const k = makeKey(d, scale);
      const val = Math.round((byKey.get(k)?.amount ?? 0) * 100) / 100;
      out.push({ d: new Date(d), label: formatKey(d, scale), amount: val });
    }
    return out;
  });

  labels = computed(() => this.points().map((p) => p.label));
  series = computed(() => this.points().map((p) => p.amount));

  constructor() {
    // (1) build chart quand le canvas existe (même pattern que le composant qui marche)
    effect(
      () => {
        if (this.canvasRef()) this.buildChart();
      },
      { allowSignalWrites: true },
    );

    // (2) update chart quand data/filtre/couleurs changent
    effect(
      () => {
        if (!this.chart || !this.canvasRef()) return;

        // data
        this.chart.data.labels = this.labels() as any;
        const ds = this.chart.data.datasets[0] as ChartDataset<"bar", number[]>;
        ds.data = this.series();

        // couleurs (fallbacks)
        const css = getComputedStyle(this.elRef.nativeElement);
        const bgAlt =
          css.getPropertyValue("--color-on-background-alt").trim() || "#666";
        const fill =
          css.getPropertyValue("--color-on-accent-alt").trim() || "#8a2be2";
        (ds as any).backgroundColor = fill;

        const xTicks = (this.chart.options.scales?.x as any)?.ticks;
        const yTicks = (this.chart.options.scales?.y as any)?.ticks;
        if (xTicks) xTicks.color = bgAlt;
        if (yTicks) yTicks.color = bgAlt;

        this.chart.update("none");
      },
      { allowSignalWrites: true },
    );
  }

  private buildChart() {
    const ctx = this.canvasRef()?.nativeElement.getContext("2d");
    if (!ctx) return;

    const css = getComputedStyle(this.elRef.nativeElement);
    const bgAlt =
      css.getPropertyValue("--color-on-background-alt").trim() || "#666";
    const fill =
      css.getPropertyValue("--color-on-accent-alt").trim() || "#8a2be2";

    const datasetCA: ChartDataset<"bar", number[]> = {
      label: "CA",
      data: this.series(),
      backgroundColor: fill,
      borderSkipped: false,
    };

    const config: ChartConfiguration<"bar", number[], string> = {
      type: "bar",
      data: { labels: this.labels(), datasets: [datasetCA] },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            ticks: { color: bgAlt, maxRotation: 0, autoSkip: true },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            grace: "5%",
            grid: { color: "#e0e0e0" },
            ticks: {
              color: bgAlt,
              callback: (v: any) =>
                `${parseNumber(v).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`,
            },
          },
        },
        plugins: { legend: { display: false } },
        layout: { padding: { left: 6, right: 12, top: 6, bottom: 6 } },
      },
    };

    this.chart?.destroy();
    this.chart = new Chart<"bar", number[], string>(ctx, config);
  }

  ngOnDestroy() {
    this.chart?.destroy();
  }
}

/* ===== Helpers temps ===== */

function parseDate(v: any): Date | null {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

function startOf(d: Date, scale: TimeScale): Date {
  const dd = new Date(d);
  if (scale === "DAY") {
    dd.setHours(0, 0, 0, 0);
    return dd;
  }
  if (scale === "MONTH")
    return new Date(dd.getFullYear(), dd.getMonth(), 1, 0, 0, 0, 0);
  // WEEK ISO → lundi
  return startOfISOWeek(dd);
}

function addOne(d: Date, scale: TimeScale): Date {
  const dd = new Date(d);
  if (scale === "DAY") {
    dd.setDate(dd.getDate() + 1);
    return startOf(dd, "DAY");
  }
  if (scale === "MONTH")
    return new Date(dd.getFullYear(), dd.getMonth() + 1, 1, 0, 0, 0, 0);
  // WEEK ISO
  dd.setDate(dd.getDate() + 7);
  return startOfISOWeek(dd);
}

function startOfISOWeek(date: Date): Date {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // 0 = lundi
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - day);
  return d;
}

function isoWeek(date: Date): { year: number; week: number } {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const dayNum = (d.getUTCDay() + 6) % 7; // 0=lundi
  d.setUTCDate(d.getUTCDate() - dayNum + 3); // jeudi
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const week =
    1 +
    Math.round(((d.getTime() - firstThursday.getTime()) / 86400000 - 3) / 7);
  return { year: d.getUTCFullYear(), week };
}

function makeKey(d: Date, scale: TimeScale): string {
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  if (scale === "DAY") return `${y}-${m}-${day}`;
  if (scale === "MONTH") return `${y}-${m}`;
  const { year, week } = isoWeek(d);
  return `${year}-W${week.toString().padStart(2, "0")}`;
}

function formatKey(d: Date, scale: TimeScale): string {
  if (scale === "DAY") {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
    }).format(d);
  }
  if (scale === "MONTH") {
    return new Intl.DateTimeFormat("fr-FR", {
      month: "2-digit",
      year: "numeric",
    }).format(d);
  }
  const { year, week } = isoWeek(d);
  return `S${week.toString().padStart(2, "0")} ${year}`;
}
